import React from 'react';
import { Modal, Form, Input, Button, FormInstance, Alert } from 'antd';

interface SegmentFormValues {
  id: string;
  duration: string;
  offset?: string;
  segment_num: number;
  asset_identifier: string;
  segment_type: string;
  media_type: string;
  title: string;
  series_code: string;
  ref_id: string;
}

/** Listing-row fields that are not editable but say where the segment came from. */
interface SegmentProvenanceFields {
  segment_identifier?: string;
  parent_feed?: string;
  video_id?: string | null;
  promo_type?: string | null;
}

interface EditSegmentModalProps {
  form: FormInstance;
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: SegmentFormValues) => void;
  initialValues: (SegmentFormValues & SegmentProvenanceFields) | null;
}

const MARATHON_PARENT_FEED = 'MarathonTools';

/**
 * "NextOn" in the title or asset identifier is how the schedule export
 * recognizes a next-on promo (an asset identifier containing it is also excluded
 * from episode content). The asset identifier is locked here, so only a title
 * edit can add or remove the flag — and only when the asset identifier does not
 * already carry it.
 */
const hasNextOnFlag = (value?: string) => /nexton/i.test(value ?? '');

type Provenance = 'cue' | 'single' | 'promo' | 'cloudport';

const provenanceOf = (record: SegmentProvenanceFields | null): Provenance | null => {
  if (!record) return null;
  const segmentId = record.segment_identifier ?? '';
  if (record.parent_feed && record.parent_feed !== MARATHON_PARENT_FEED) return 'cloudport';
  if (segmentId.startsWith('cue_') || record.video_id) return 'cue';
  if (segmentId.startsWith('promo_') || record.promo_type) return 'promo';
  if (segmentId.startsWith('single_')) return 'single';
  return null;
};

/** Every source that produces segments also rewrites them; edits made here only last until then. */
const PROVENANCE_NOTES: Record<Provenance, string> = {
  cue:
    'Sliced from a video. Re-slicing that video (bulk segmentation with "redo existing", or a segment redo) ' +
    'regenerates the Title from the Asset Identifier and overwrites edits made here.',
  single:
    'Created by a single-segment upload. Uploading the same asset again overwrites the Title.',
  promo:
    'Materialized from the promo registry. The next promo sync overwrites the Title from the registry\'s Amagi ID.',
  cloudport:
    'Imported from Amagi CloudPort. The next CloudPort refresh replaces this row and discards edits.',
};

const ASSET_IDENTIFIER_LOCKED_NOTE =
  'Read-only. The slicer matches re-slices on this value, the export and scheduler read the "NextOn" flag ' +
  'from it, playout uses it as the item id where no video id exists, and for Amagi rows it is Amagi\'s house ' +
  'ID. Re-slice or re-upload the asset to change it.';

const EditSegmentModal: React.FC<EditSegmentModalProps> = ({ form, visible, onClose, onSubmit, initialValues }) => {
  const title = Form.useWatch('title', form);

  const assetFlagged = hasNextOnFlag(initialValues?.asset_identifier);
  const flaggedBefore = assetFlagged || hasNextOnFlag(initialValues?.title);
  const flaggedNow = assetFlagged || hasNextOnFlag(title);
  const nextOnChange = flaggedBefore === flaggedNow ? null : flaggedNow ? 'adding' : 'removing';

  const provenance = provenanceOf(initialValues);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      values.id = initialValues?.id; // Ensure the ID is set from initial values
      onSubmit(values);
      onClose();
    } catch (error) {
      console.error('Validation Failed:', error);
    }
  };

  return (
    <Modal
      title="Edit Segment"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleOk}>
          Save
        </Button>,
      ]}
    >
      {provenance && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Edits here do not survive regeneration"
          description={PROVENANCE_NOTES[provenance]}
        />
      )}
      {nextOnChange && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`You are ${nextOnChange} the "NextOn" flag`}
          description={
            '"NextOn" in the Title is how the schedule export recognizes a next-on promo. This change alters ' +
            'how the segment airs, not just how it is labelled.'
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues ?? undefined}
      >
        <Form.Item
          name="id"
          hidden
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: 'Please enter the title' }]}
        >
          <Input placeholder="Enter title" />
        </Form.Item>
        <Form.Item
          label="Duration"
          name="duration"
          rules={[{ required: true, message: 'Please enter the duration' }]}
        >
          <Input placeholder="Enter duration" />
        </Form.Item>
        <Form.Item
          label="Offset"
          name="offset"
        >
          <Input placeholder="Enter offset" />
        </Form.Item>
        <Form.Item
          label="Segment Number"
          name="segment_num"
          rules={[{ required: true, message: 'Please enter the segment number' }]}
        >
          <Input type="number" placeholder="Enter segment number" />
        </Form.Item>
        <Form.Item
          label="Asset Identifier"
          name="asset_identifier"
          extra={ASSET_IDENTIFIER_LOCKED_NOTE}
        >
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="Segment Type"
          name="segment_type"
          rules={[{ required: true, message: 'Please enter the segment type' }]}
        >
          <Input placeholder="Enter segment type" />
        </Form.Item>
        <Form.Item
          label="Media Type"
          name="media_type"
          rules={[{ required: true, message: 'Please enter the media type' }]}
        >
          <Input placeholder="Enter media type" />
        </Form.Item>
        <Form.Item
          label="Series Code"
          name="series_code"
          rules={[{ required: true, message: 'Please enter the series code' }]}
        >
          <Input placeholder="Enter series code" />
        </Form.Item>
        <Form.Item
          label="REF ID"
          name="ref_id"
          rules={[{ required: true, message: 'Please enter the REF ID' }]}
        >
          <Input placeholder="Enter REF ID" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditSegmentModal;
